import { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { Stack, Button, Divider, Grid } from '@mui/material_modern'
import { getImageUrl } from 'services/assets'
import { trackEvent } from 'utils/tracking'

// components
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import DispatchTicketImagesLightbox from 'components/containers/load-board/DispatchTicketContainer/DispatchTicketImagesLightbox'
import CapturePhotoModal from 'components/generic/modals/dialog-modal/CapturePhotoModal'

// utils
import { CHURN_ZERO_EVENTS, GS_ERROR_CODE } from 'utils/constants'
import { dispatchSnackbar } from 'utils/helpers'
import { setSnackBar } from 'state/ui/index'
import { useUploadOrgImageMutation } from 'services/assets'
import { Skeleton } from '@mui/material'
import { CameraAlt, CloudDownload, DeleteForever } from '@mui/icons-material_modern'
import { useTheme } from '@mui/material/styles'
import { useUpdateDispatchTicketMutation } from '../../../../services/dispatch.ts'
import { DispatchTicketImage, DispatchTicketSchema } from '../../../../gen/rtk-openapi.ts'
import { useAppSelector } from '../../../../utils/custom-hooks/useTypedReduxStore.ts'
import { selectModalProps } from '../../../../state/ui/selectors.ts'

const imageStyles = {
  width: '100%',
  borderRadius: '4px',
  transform: 'unset',
  cursor: 'pointer',
}

export interface ImageUrlSchema {
  url: string
  filename: string
}

interface DispatchTicketImagesBladeProps {
  dispatchTicketData: DispatchTicketSchema
}

const DispatchTicketImagesBlade = ({ onHideModal }) => {
  const dispatch = useDispatch()
  const { inOrgMode } = useAppSelector(state => state.ui)
  const { yardDetailsList } = useAppSelector(state => state.auth)
  const { dispatchTicketData } = useAppSelector<DispatchTicketImagesBladeProps>(selectModalProps)
  const methods = useForm()
  const [uploadOrgImage, { isLoading }] = useUploadOrgImageMutation()
  const theme = useTheme()

  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [imageUrls, setImageUrls] = useState<ImageUrlSchema[]>([])
  const [openCapturePhotoModal, setOpenCapturePhotoModal] = useState(false)
  const [images, setImages] = useState<DispatchTicketImage[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [updateDispatchTicket] = useUpdateDispatchTicketMutation()

  const updateTicket = async newImages => {
    const orgModeYard = yardDetailsList?.find(item => item.id === dispatchTicketData?.yardId)

    await updateDispatchTicket({
      updateDispatchTicketBody: {
        ...dispatchTicketData,
        images: newImages,
      },
      yard: inOrgMode ? orgModeYard?.name : '',
      dispatchTicketId: dispatchTicketData.id ?? '',
    })
      .unwrap()
      .then(() => dispatchSnackbar(dispatch, 'success', 'Images successfully edited.'))
      .catch(error => {
        let errorMessage = 'There was an error editing your images.'

        if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
          errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again`
        }

        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
  }

  const handleDownload = imageUrl => {
    const link = document.createElement('a')
    link.href = imageUrl.url
    link.download = imageUrl.filename
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.click()
  }

  const handleSaveImages = filename => {
    const newImages: DispatchTicketImage[] = images.concat({
      filename: filename,
      uploadedTime: new Date().toISOString(),
    })

    updateTicket(newImages)
    setImages(newImages)
  }

  const handleRemoveImage = imageUrl => {
    const newImages: DispatchTicketImage[] = images.filter(image => {
      return image.filename !== imageUrl.filename
    })

    updateTicket(newImages)
    setImages(newImages)
  }

  const handleFileChange = async event => {
    const file = event.target.files[0]
    if (!file) {
      return
    }

    const formData = new FormData()
    formData.append('image', file)

    await uploadOrgImage({ body: formData })
      .unwrap()
      .then(response => {
        handleSaveImages(response.imageName)
      })
      .catch(() => dispatchSnackbar(dispatch, 'error', 'An error occurred while uploading your photo'))
  }

  const handleS3Error = error => {
    const errorMsg = error?.data?.error || 'There was an error getting an image'
    trackEvent({ eventName: CHURN_ZERO_EVENTS.GET_S3_IMAGE, errorMsg })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  useEffect(() => {
    // function to fetch signed urls for images
    const fetchImageSignedUrls = async ticketImages => {
      try {
        const newImageUrls: ImageUrlSchema[] = await Promise.all(
          ticketImages.map(async ticketImage => {
            return {
              url: await getImageUrl(ticketImage.filename),
              filename: ticketImage.filename,
            }
          }),
        )
        setImageUrls(newImageUrls)
      } catch (error) {
        handleS3Error(error)
      }
    }

    // call function to initiate async call to fetch signed urls
    fetchImageSignedUrls(images)
  }, [images])

  useEffect(() => {
    if (dispatchTicketData) {
      setImages(dispatchTicketData.images ?? [])
    }
  }, [dispatchTicketData])

  const renderImage = imageUrl => {
    return imageUrl.url ? (
      <div
        style={{
          position: 'relative',
          paddingTop: '6px',
          paddingLeft: '10px',
          paddingRight: '10px',
        }}
        key={imageUrl.filename}
      >
        <img src={imageUrl.url} alt="" style={imageStyles} onClick={() => setLightboxOpen(true)} />
        <Button
          variant="contained"
          size="small"
          color="error"
          onClick={() => handleRemoveImage(imageUrl)}
          style={{ position: 'absolute', top: '10px', right: '16px' }}
        >
          <DeleteForever />
        </Button>
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() => handleDownload(imageUrl)}
          style={{ position: 'absolute', top: '10px', right: '86px' }}
        >
          <CloudDownload />
        </Button>
      </div>
    ) : (
      <div
        style={{
          padding: '30px',
        }}
      >
        <CameraAlt
          style={{
            ...imageStyles,
            color: theme.palette.text.disabled,
            border: 1,
            borderStyle: 'solid',
            borderColor: theme.palette.text.disabled,
            height: 'unset',
          }}
        />
      </div>
    )
  }

  const renderContent = () => {
    if (isLoading || imageUrls.length < images.length) {
      return (
        <Skeleton
          animation="wave"
          sx={{
            ...imageStyles,
            paddingTop: '24px',
            width: '80%',
            height: '25px',
            alignSelf: 'center',
          }}
        />
      )
    }

    return imageUrls?.length > 0 ? (
      <Grid container spacing={2} style={{ padding: '10px', overflowY: 'auto' }}>
        {imageUrls.map(imageUrl => (
          <Grid item xs={12} sm={6} key={imageUrl.filename}>
            {renderImage(imageUrl)}
          </Grid>
        ))}
      </Grid>
    ) : (
      renderImage('')
    )
  }

  return (
    <FormProvider {...methods}>
      <form>
        <Stack height="100vh" width="100%" justifyContent="space-between">
          <Stack>
            <FormModalHeading
              title={`Dispatch Ticket #${dispatchTicketData?.externalId} Images`}
              backButtonPosition="right"
            />
            <Divider />
          </Stack>
          <Stack height="100%" overflow="auto">
            {renderContent()}
          </Stack>
          <Stack direction="row" p={2} spacing={2} alignItems="center">
            <Button variant="contained" color={'primary'} onClick={() => onHideModal({ skipBlocker: true })}>
              Done
            </Button>
            <Button variant="outlined" color={'primary'} onClick={handleUploadClick}>
              Upload Image
            </Button>
            <Button variant="outlined" color={'primary'} onClick={() => setOpenCapturePhotoModal(true)}>
              Capture Image
            </Button>
          </Stack>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileChange}
          />
          <DispatchTicketImagesLightbox
            lightboxOpen={lightboxOpen}
            dispatchTicketImages={images}
            onClose={() => setLightboxOpen(false)}
          />
          {openCapturePhotoModal && (
            <CapturePhotoModal
              title="Take a Photo"
              hideDividers
              open={openCapturePhotoModal}
              onSave={filename => {
                handleSaveImages(filename)
              }}
              onClose={() => setOpenCapturePhotoModal(false)}
              dialogStyles={{
                '& .MuiDialog-paper': { width: '100%', maxWidth: 600 },
              }}
            />
          )}
        </Stack>
      </form>
    </FormProvider>
  )
}

export default DispatchTicketImagesBlade
